<template>
  <v-container fluid fill-height grid-list-lg>
    <v-layout row wrap align-content-start>
      <h3 v-if="imagesLoaded && approvedImages.length <= 0">{{ $t('vendors.approved.nonApprovedImages') }}</h3>
      <v-flex d-flex xs12 sm6 md4 lg2 v-for="image in approvedImages" :key="image.id">
        <v-card class="thumb-image">
          <v-img
            :class="getInvisibleClass(image)"
            v-on:click="imageClick(image)"
            contain
            :src="getThumbnailPath(image)"
            height="200px"
          >
            <div class="d-flex justify-space-between pa-2">
              <v-icon class="invisible">{{ isVisible(image) ? '' : 'mdi-eye-off' }}</v-icon>
              <!-- <v-icon class="invisible">mdi-eye-off</v-icon> -->
              <v-icon class="approved">mdi-checkbox-marked-circle-outline</v-icon>
            </div>
            <v-layout slot="placeholder" fill-height align-center justify-center ma-0>
              <v-progress-circular indeterminate color="grey"></v-progress-circular>
            </v-layout>
          </v-img>

          <v-card-actions class="card-action">
            <div class="d-flex" style="flex-direction: column">
              <div class="d-flex" style="flex-direction: row">
                <span class="card-action-tag-item">
                  <v-icon>mdi-tag</v-icon>
                  {{ image.tags.length }}
                </span>
                <span class="card-action-category-item">
                  <v-icon>mdi-layers</v-icon>
                  {{ image.categories.length === 0 ? $t('categoryMissing') : pickLocaleDescriptionValue(image.categories[0]) }}
                </span>
              </div>
              <div class="mt-2">{{ $t('vendors.approved.nrOfDownloads', { count: image.downloadlogs_count}) }}</div>
              <div class="mt-3">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="secondary"
                      dark
                      small
                      v-bind="attrs"
                      v-on="on"
                      style="min-width: 30px"
                      @click="onChangeVisibility(image)"
                    >
                      <v-icon dense>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('vendors.approved.changeVisibility') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="secondary"
                      dark
                      small
                      v-bind="attrs"
                      v-on="on"
                      style="min-width: 30px"
                      @click="openImageDialogInEditMode(image)"
                    >
                      <v-icon dense>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('vendors.approved.editImage') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="secondary"
                      dark
                      small
                      v-bind="attrs"
                      v-on="on"
                      style="min-width: 30px"
                      @click="openImageDialogInDeleteMode(image)"
                    >
                      <v-icon dense>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('vendors.approved.deleteImage') }}</span>
                </v-tooltip>
              </div>
            </div>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>

    <image-edit-dialog-component
      :visible="showImageDialog"
      :image="selectedImage"
      :mode="imageDialogStartMode"
      @updated="onImageDeleted"
      @deleted="onImageDeleted"
      @close="onCloseDialog"
    ></image-edit-dialog-component>
  </v-container>
</template>

<script>
import { FileService } from '../../../services/file.service';
import ImageEditDialogComponent from '../../../components/dialog/ImageEditDialogComponent.vue';
import { pickLocaleDescription } from '../../../services/locale.service';

const APPROVED = 3;
const APPROVED_HIDDEN = 6;

export default {
  components: {
    ImageEditDialogComponent,
  },

  data() {
    return {
      approvedImages: [],
      imagesLoaded: false,
      selectedImage: undefined,
      showImageDialog: false,
      imageDialogStartMode: '',
    };
  },

  methods: {
    pickLocaleDescriptionValue(item) {
      return pickLocaleDescription(item)
    },

    getThumbnailPath(image) {
      return `${image.path}/thumb/thumb_sm_${image.thumb_name.substring(6)}`;
    },

    getInvisibleClass(image) {
      return this.isVisible(image) ? '' : 'image-invisible';
    },

    isVisible(image) {
      return image.status.id === APPROVED;
    },

    reload() {
      new FileService().getApproved().then((data) => {
        this.imagesLoaded = true;
        this.approvedImages = data.data;
      });
    },

    imageClick(image) {
      this.imageDialogStartMode = '';
      this.selectedImage = image;
      this.showImageDialog = true;
    },

    onImageDeleted() {
      const selectedImageIndex = this.approvedImages.indexOf(this.selectedImage);
      this.approvedImages.splice(selectedImageIndex, 1);
    },

    onCloseDialog() {
      this.showImageDialog = false;
      this.imageDialogStartMode = '';
    },

    onChangeVisibility(image) {
      const toggledVisibility = image.status.id === APPROVED_HIDDEN;
      new FileService()
        .updateVisibility(image.id, toggledVisibility) // toggle visibility
        .then((response) => {
          if (response.success) {
            this.approvedImages = this.approvedImages.map((persistedImage) => {
              if (persistedImage.id != image.id) {
                return persistedImage;
              }
              return { ...persistedImage, status: response.status };
            });
          } else {
            console.error('Man får inte ändra synlighetsstatus på icke godkända bilder', image.id);
          }
        });
    },

    openImageDialogInEditMode(image) {
      this.imageDialogStartMode = 'edit';
      this.selectedImage = image;
      this.showImageDialog = true;
    },

    openImageDialogInDeleteMode(image) {
      this.imageDialogStartMode = 'delete';
      this.selectedImage = image;
      this.showImageDialog = true;
    },
  },
};
</script>

<style lang="scss" scoped>
h3 {
  margin: 20px 0;
}

i.approved {
  color: green;
  font-size: 60px;
}

i.invisible {
  font-size: 60px;
}

.thumb-image .v-image:hover {
  cursor: pointer;
}
</style>

<style lang="css">
.thumb-image .image-invisible .v-image__image {
  opacity: 0.5;
  filter: grayscale(1);
}
</style>
